import { IDBPDatabase } from 'idb'
import { BaseIndexedDB, FileMetadataEntry, STORE_NAME } from './BaseIndexedDB'

/**
 * Non-Safari strategy. Persists native Blobs, which IndexedDB stores by reference and
 * returns lazily on read — so a cursor scan never pulls blob bytes into memory and the
 * generic `list()` already serves metadata cheaply. No index is needed.
 */
export class DefaultIndexedDB extends BaseIndexedDB {
  protected get schemaVersion(): number {
    return 1
  }

  protected createSchema(db: IDBPDatabase): void {
    if (!db.objectStoreNames.contains(STORE_NAME)) {
      db.createObjectStore(STORE_NAME) // external key, no keyPath
    }
  }

  protected async toStorable<T extends object>(value: T): Promise<T> {
    // Store the record (and its native Blob) as-is.
    return value
  }

  protected async hydrateForRead<T>(value: T): Promise<T> {
    const v = value as any
    if (!v) return value
    const b = v.blob
    // Legacy records persisted as ArrayBuffers (e.g. by an older Safari-format build) are
    // wrapped back into a Blob. Native Blobs are returned AS-IS to preserve laziness —
    // re-reading them via arrayBuffer() here would defeat IndexedDB's lazy blob storage.
    if (typeof ArrayBuffer !== 'undefined' && b instanceof ArrayBuffer) {
      v.blob = new Blob([b], { type: v._blobType || '' })
    }
    return value
  }

  async listMetadata(prefix: string): Promise<FileMetadataEntry[]> {
    const records = await this.list(prefix)
    const out: FileMetadataEntry[] = []
    for (const [path, rec] of records) {
      if (rec.isFolder) continue
      out.push({
        path,
        systemFileNumber: rec.systemFileNumber,
        modificationDate: rec.modificationDate,
        size: rec.size,
        isFolder: false,
      })
    }
    return out
  }
}
